import { useNotifications } from '@sequencehq/core-components'
import { CubeDomainInterface } from 'modules/Cube/domain/cube.domain'
import {
  CorePortErrors,
  CubePortImplementation,
  CubePortImplementationProp,
  QuoteTemplateCubePorts
} from 'modules/Cube/domain/cube.domain.types'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { quoteTemplateAdapters } from './adapters/quoteTemplate.adapters'
import { useLoadQuoteTemplateEditor } from './ports/useLoadQuoteTemplateEditor'
import { useSaveQuoteTemplateEditor } from './ports/useSaveQuoteTemplateEditor'

type UseQuoteTemplatePorts = (props: {
  quoteTemplateId?: string
}) => CubePortImplementation

export const useQuoteTemplatePorts: UseQuoteTemplatePorts = props => {
  const quoteTemplateLoader = useLoadQuoteTemplateEditor()
  const saveQuoteTemplateEditor = useSaveQuoteTemplateEditor()
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()

  const [existingData, setExistingData] =
    useState<CubeDomainInterface['queries']['rawData']['data']>()

  const saveQuoteTemplate = useCallback(
    async (
      domainOutput: CubeDomainInterface['queries']
    ): Promise<{
      success: boolean
      id?: string
    }> => {
      if (!existingData) {
        throw new Error("Can't save without initialising first")
      }

      const dataToSave = quoteTemplateAdapters.toApi(domainOutput.rawData.data)

      if (domainOutput.rawData.data.common.id !== 'uninitialized') {
        try {
          const saveResult = await saveQuoteTemplateEditor.updateQuoteTemplate(
            domainOutput.rawData.data.common.id
          )(dataToSave)

          if (saveResult.success) {
            displayNotification('Template updated', {
              type: 'success'
            })
            return {
              success: true,
              id: saveResult.quoteTemplate?.template.id
            }
          }

          displayNotification('Failed to save template', {
            type: 'error'
          })
          return {
            success: false
          }
        } catch {
          displayNotification('Failed to save template', {
            type: 'error'
          })
          return {
            success: false
          }
        }
      } else {
        displayNotification('Creating template...', {
          duration: 30000
        })

        const createResult = await saveQuoteTemplateEditor.createQuoteTemplate()
        if (createResult.success) {
          displayNotification('Template created', {
            type: 'success'
          })

          if (createResult.quoteTemplate?.template.id) {
            navigate(
              `/quote-templates/${createResult.quoteTemplate.template.id}`,
              {
                replace: true
              }
            )
          }
        }
        if (!createResult.success) {
          displayNotification('Failed to save template', {
            type: 'error'
          })
        }
        return {
          success: createResult.success,
          id: createResult.quoteTemplate?.template.id
        }
      }
    },
    [existingData, navigate, displayNotification, saveQuoteTemplateEditor]
  )

  const loadQuoteTemplate = useCallback(
    (ctx: CubePortImplementationProp) => async () => {
      const { data: quoteTemplateData, error: quoteTemplateError } =
        await quoteTemplateLoader(ctx)(props.quoteTemplateId)

      if (quoteTemplateError || !quoteTemplateData) {
        return {
          data: null,
          error: quoteTemplateError || CorePortErrors.Other
        }
      }

      const resolvedData = quoteTemplateAdapters.toCube(quoteTemplateData)

      if (resolvedData.data.common.id !== props.quoteTemplateId) {
        navigate(`/quote-templates/${resolvedData.data.common.id}`, {
          replace: true
        })
      }

      setExistingData(resolvedData.data)

      const data = await Promise.resolve({
        data: resolvedData.data,
        configuration: {
          ...ctx.configuration,
          currency: {
            default: quoteTemplateData.currencies[0],
            enabled: quoteTemplateData.currencies
          }
        }
      })

      return {
        data,
        error: null
      }
    },
    [quoteTemplateLoader, props.quoteTemplateId, navigate]
  )

  const portImplementation: CubePortImplementation = useCallback(
    (ctx): QuoteTemplateCubePorts => ({
      in: {
        core: loadQuoteTemplate(ctx),
        customer: () => Promise.reject()
      },
      out: {
        save: saveQuoteTemplate,
        /**
         * We handle this as part of the menu item for quote templates - we'll eventually
         * refactor other ports as well as remove this entirely.
         * @returns
         */
        archive: () => Promise.reject()
      }
    }),
    [loadQuoteTemplate, saveQuoteTemplate]
  )

  return portImplementation
}
